import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Info,
  Ban,
  Database,
  Filter,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import type {
  PlanSelectorDiagnostics,
  PlanSelectorRejectionReason,
} from "../../types/run";

/**
 * Plan-selector rejection banner.
 *
 * Shown only when the BE plan endpoint returned `selector.yielded === 0`
 * (i.e. zero candidates after selector pipeline). The BE picks a single
 * `rejection_reason` according to its priority order — we trust that
 * choice and just map it to a human-friendly title/hint/icon/tone here.
 *
 * The UI is intentionally category-driven (input/scope/data/filter) so
 * the user immediately sees whether they can fix this themselves
 * (input/filter = yes, data/scope = not from here).
 */
interface PlanSelectorBannerProps {
  selector: PlanSelectorDiagnostics;
  /** Optional — invoked when user clicks the CTA for filter-category reasons. */
  onRelaxFilters?: () => void;
}

type Tone = "info" | "warning" | "error";
type Category = "input" | "scope" | "data" | "filter" | "unknown";

/**
 * Rejection reasons are wire codes the backend picks; only the title, hint and
 * CTA text are translated, keyed by the same code.
 */
type ReasonSlug =
  | "inputNoImages"
  | "inputImagesNotFound"
  | "scopeOwnershipDenied"
  | "dataNoClassifications"
  | "dataLegacyLabelsOnly"
  | "dataBinaryPairConflict"
  | "dataMetadataIncomplete"
  | "filterConfidenceExcludedAll"
  | "filterOnlyMistakesEmpty"
  | "unknown";

type CtaKey =
  | "selectorReason.cta.clearConfidence"
  | "selectorReason.cta.disableFilter";

interface ReasonPresentation {
  slug: ReasonSlug;
  category: Category;
  tone: Tone;
  cta?: { labelKey: CtaKey; action: "relax_filters" };
}

const REASON_PRESENTATION: Record<
  PlanSelectorRejectionReason,
  ReasonPresentation
> = {
  INPUT_NO_IMAGES: {
    slug: "inputNoImages",
    category: "input",
    tone: "info",
  },
  INPUT_IMAGES_NOT_FOUND: {
    slug: "inputImagesNotFound",
    category: "input",
    tone: "warning",
  },
  SCOPE_OWNERSHIP_DENIED: {
    slug: "scopeOwnershipDenied",
    category: "scope",
    tone: "error",
  },
  DATA_NO_CLASSIFICATIONS: {
    slug: "dataNoClassifications",
    category: "data",
    tone: "warning",
  },
  DATA_LEGACY_LABELS_ONLY: {
    slug: "dataLegacyLabelsOnly",
    category: "data",
    tone: "warning",
  },
  DATA_BINARY_PAIR_CONFLICT: {
    slug: "dataBinaryPairConflict",
    category: "data",
    tone: "warning",
  },
  DATA_METADATA_INCOMPLETE: {
    slug: "dataMetadataIncomplete",
    category: "data",
    tone: "warning",
  },
  FILTER_CONFIDENCE_EXCLUDED_ALL: {
    slug: "filterConfidenceExcludedAll",
    category: "filter",
    tone: "info",
    cta: { labelKey: "selectorReason.cta.clearConfidence", action: "relax_filters" },
  },
  FILTER_ONLY_MISTAKES_EMPTY: {
    slug: "filterOnlyMistakesEmpty",
    category: "filter",
    tone: "info",
    cta: { labelKey: "selectorReason.cta.disableFilter", action: "relax_filters" },
  },
  UNKNOWN: {
    slug: "unknown",
    category: "unknown",
    tone: "error",
  },
};

const TONE_STYLES: Record<Tone, string> = {
  info: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-200",
  warning:
    "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200",
  error:
    "border-destructive/40 bg-destructive/10 text-destructive dark:text-red-300",
};

const CATEGORY_ICON: Record<Category, typeof Info> = {
  input: Info,
  scope: Ban,
  data: Database,
  filter: Filter,
  unknown: AlertTriangle,
};

const PlanSelectorBanner = ({
  selector,
  onRelaxFilters,
}: PlanSelectorBannerProps) => {
  const { t } = useTranslation("mlExports");
  const [funnelOpen, setFunnelOpen] = useState(false);

  // Defensive fallback: if BE returned yielded === 0 but no reason code.
  const reason: PlanSelectorRejectionReason =
    selector.rejection_reason ?? "UNKNOWN";
  const presentation = REASON_PRESENTATION[reason];
  const Icon = CATEGORY_ICON[presentation.category];

  const { input, scope, data, filters, yielded } = selector;

  // Funnel rows — only show steps that actually dropped something.
  const funnelRows: Array<{ label: string; value: number; dropped?: number }> = [
    { label: t("selectorFunnel.requested"), value: input.requested },
    { label: t("selectorFunnel.foundInDb"), value: input.found_in_db },
  ];
  if (scope.ownership_denied > 0) {
    funnelRows.push({
      label: t("selectorFunnel.ownershipDenied"),
      value: scope.ownership_denied,
      dropped: scope.ownership_denied,
    });
  }
  if (data.no_classifications > 0) {
    funnelRows.push({
      label: t("selectorFunnel.noClassifications"),
      value: data.no_classifications,
      dropped: data.no_classifications,
    });
  }
  if (data.legacy_labels_only > 0) {
    funnelRows.push({
      label: t("selectorFunnel.legacyLabelsOnly"),
      value: data.legacy_labels_only,
      dropped: data.legacy_labels_only,
    });
  }
  if (data.binary_pair_conflict > 0) {
    funnelRows.push({
      label: t("selectorFunnel.binaryPairConflict"),
      value: data.binary_pair_conflict,
      dropped: data.binary_pair_conflict,
    });
  }
  if (data.metadata_incomplete > 0) {
    funnelRows.push({
      label: t("selectorFunnel.metadataIncomplete"),
      value: data.metadata_incomplete,
      dropped: data.metadata_incomplete,
    });
  }
  if (filters.confidence_excluded > 0) {
    funnelRows.push({
      label: t("selectorFunnel.confidenceFilter"),
      value: filters.confidence_excluded,
      dropped: filters.confidence_excluded,
    });
  }
  if (filters.only_mistakes_excluded > 0) {
    funnelRows.push({
      label: t("selectorFunnel.onlyMistakesFilter"),
      value: filters.only_mistakes_excluded,
      dropped: filters.only_mistakes_excluded,
    });
  }
  funnelRows.push({ label: t("selectorFunnel.final"), value: yielded });

  // Prefer the BE-provided message if present — it's the source of truth.
  // Fall back to our static hint if the BE left it null.
  const hint =
    selector.rejection_message ??
    t(`selectorReason.hints.${presentation.slug}` as "selectorReason.hints.unknown");

  return (
    <div
      className={cn(
        "rounded-md border p-3 text-xs space-y-2",
        TONE_STYLES[presentation.tone]
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold">
            {t(
              `selectorReason.titles.${presentation.slug}` as "selectorReason.titles.unknown",
            )}
          </div>
          <div className="mt-0.5 opacity-90">{hint}</div>
        </div>
      </div>

      {presentation.cta && onRelaxFilters && (
        <div className="pl-6">
          <button
            onClick={onRelaxFilters}
            className="underline underline-offset-2 font-medium hover:opacity-80"
          >
            {t(presentation.cta.labelKey)}
          </button>
        </div>
      )}

      <div className="pl-6">
        <button
          onClick={() => setFunnelOpen((v) => !v)}
          className="inline-flex items-center gap-1 opacity-80 hover:opacity-100"
        >
          {funnelOpen ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          {t("selectorFunnel.toggle", {
            action: funnelOpen
              ? t("selectorFunnel.hide")
              : t("selectorFunnel.show"),
          })}
        </button>

        {funnelOpen && (
          <div className="mt-2 rounded border border-current/20 bg-background/40 p-2 font-mono space-y-0.5">
            {funnelRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between">
                <span className="opacity-80">{row.label}</span>
                <span>
                  {formatNumber(row.value)}
                  {row.dropped != null && row.dropped > 0 && (
                    <span className="opacity-60">
                      {t("selectorFunnel.dropped", {
                        value: formatNumber(row.dropped),
                      })}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanSelectorBanner;
